import React, { useEffect, useRef, useState } from "react";
import { createThirdpartyQuestionsService, editThirdpartyQuestionsService, getTprmAssessmentCategoriesService, get_questionnaires } from "../../services";
import ButtonLoader from "../../Components/ButtonLoader";
import CommonModal from "../../Components/CommonModal";
import CommonInput from "../../Components/CommonInput";
import CommonDropdown from "../../Components/CommonDropdown";
import CommonButton from "../../Components/CommonButton";

// Custom-mode input + its Cancel button need a matching fixed height — a
// stylesheet specificity conflict elsewhere in the app (`.common_button
// button` beats `.cmn_btn`) makes CommonButton render taller than
// `.cmn-modal-input` unless pinned explicitly. Same values used in
// BulkUploadQuestionsModal.js's equivalent field for visual parity.
const CUSTOM_FIELD_HEIGHT = 38;
const CUSTOM_INPUT_STYLE = { height: CUSTOM_FIELD_HEIGHT, padding: "0 12px" };
const CUSTOM_CANCEL_BTN_STYLE = { height: CUSTOM_FIELD_HEIGHT };

const RESPONSE_TYPE_OPTIONS = [
  { value: "yes_no", label: "Yes/No" },
  { value: "multi_select", label: "Multi Select" },
];

// Raw internal response_type values the backend can return directly (as
// opposed to a human-readable response_type_display string). Editing a
// question whose record has no response_type_display — or where the display
// map below doesn't recognize it — must still recognize an already-raw
// value like "multi_select" instead of falling through to the "yes_no"
// default and silently wiping multi_select_options on save.
const RAW_RESPONSE_TYPES = new Set(["yes_no", "multi_select", "text", "file_upload", "rating"]);

const RESPONSE_TYPE_MAP = {
  "Yes / No": "yes_no",
  "Yes/No": "yes_no",
  "Multi-select": "multi_select",
  "Multi Select": "multi_select",
  "Text": "text",
  "File Upload": "file_upload",
  "Rating (1-5)": "rating",
  "Rating": "rating",
};

const WEIGHT_OPTIONS = [
  { value: 1, label: "1 (Lowest)" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
  { value: 5, label: "5 (Highest)" },
];

function resolveResponseType(editingQuestion) {
  if (!editingQuestion) return "yes_no";
  const rawType = editingQuestion.response_type;
  return (
    (rawType && RAW_RESPONSE_TYPES.has(rawType) && rawType) ||
    RESPONSE_TYPE_MAP[editingQuestion.response_type_display] ||
    RESPONSE_TYPE_MAP[rawType] ||
    "yes_no"
  );
}

// Config-driven fields (CommonModal's `fields=`/`defaultValues=`/`onSubmit=`
// mode). Questionnaire Name/Category are back in `fields=`, in their
// original positions, using 2 small additive CommonModal.js capabilities:
//  - a select field's config can supply an `onChange`, fired alongside its
//    own react-hook-form update — used here to flip into "custom" mode the
//    moment "Custom (Enter your own)" is picked (previously undetectable
//    from outside CommonModal.js at all)
//  - a text field's config can supply a `suffixButton`, rendering a real
//    CommonButton to the right of the input in the same row — the actual
//    "Cancel" button, matching BulkUploadQuestionsModal.js's layout exactly
//    (CommonModal's Field renderer otherwise has no second slot beside the
//    input itself)
// Picking "custom" swaps that array slot to a different field config (a
// text field instead of a select) rather than trying to keep one field's
// *type* dynamic — each mode uses its own field name
// (questionnaire_name / questionnaire_name_custom, category / category_custom)
// so the custom text box always starts blank instead of inheriting whatever
// react-hook-form last stored for the select.
// Clicking "Cancel" needs to reset the *select* field's value back to a real
// option (not leave it on "custom") — react-hook-form's setValue isn't
// exposed by default, so this uses CommonModal's `formRef` escape hatch
// (a ref that CommonModal stashes { setValue, reset } onto) to do that one
// surgical reset without disturbing any other field's current value.
//
// Response Type + the Multi-select Options builder stay manual `children` —
// no way to read another field's live value from `fields=` config without
// exposing react-hook-form's `watch` to the caller, so Multi-select's
// visibility (gated on Response Type's live value) can't be driven from a
// `fields=` entry. This is the one remaining position change from the
// original: since `children` always renders after `fields=`, these two
// still land after Weight instead of their original spot between Question
// Text and Expected Evidence.
function CreateTPRMQuestionModal({ show, onHide, onSuccess, editingQuestion }) {
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [allCategoryKeys, setAllCategoryKeys] = useState([]);
  const [questionnaireOptions, setQuestionnaireOptions] = useState([]);
  const [optionsReady, setOptionsReady] = useState(false);

  const [isCustomQuestionnaire, setIsCustomQuestionnaire] = useState(false);
  const [isCustomCategory, setIsCustomCategory] = useState(false);

  // Tracks the last real (non-"custom") dropdown value for each field, so
  // Cancel can restore it instead of guessing. Seeded from the editing
  // question's actual value below (when it exists in the current option
  // list) — otherwise Cancel would silently discard a legacy value that
  // doesn't match any option, or drop whatever the user had picked before
  // switching to custom mode.
  const lastQuestionnaireValueRef = useRef("");
  const lastCategoryValueRef = useRef("");

  const [responseType, setResponseType] = useState("yes_no");
  const [multiSelectOptions, setMultiSelectOptions] = useState([]);
  const [multiSelectError, setMultiSelectError] = useState("");

  const formApiRef = useRef(null);

  // Fetch categories + questionnaires, then reveal the modal — same
  // optionsReady gating as before, still needed for the fields= entries
  // (Sub Category through Weight) whose edit-mode defaults depend on these
  // lists being loaded before CommonModal snapshots defaultValues.
  useEffect(() => {
    if (!show) { setOptionsReady(false); return undefined; }
    let cancelled = false;
    setOptionsReady(false);

    const load = async () => {
      try {
        const [catRes, qRes] = await Promise.all([
          getTprmAssessmentCategoriesService(),
          get_questionnaires(),
        ]);
        if (cancelled) return;

        const catData = catRes?.statusCode?.response_data?.data;
        if (catData) {
          const options = (catData.organization_categories || []).map((cat) => ({ value: cat.key, label: cat.label }));
          options.push({ value: "custom", label: "Custom (Enter your own)" });
          setCategoryOptions(options);
          setAllCategoryKeys(catData.all_keys || []);
        }
        const qData = qRes?.statusCode?.response_data?.data;
        if (qData?.questionnaires) {
          const options = qData.questionnaires.map((q) => ({ value: q.name, label: q.name }));
          options.push({ value: "custom", label: "Custom (Enter your own)" });
          setQuestionnaireOptions(options);
        }
      } catch (error) {
        console.error("Error loading question form options:", error);
      } finally {
        if (!cancelled) setOptionsReady(true);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [show]);

  // Populate everything once options are ready — including whether
  // Questionnaire Name/Category should start in custom mode (a legacy value
  // not found in the org's current list).
  useEffect(() => {
    if (!show || !optionsReady) return;
    setSubmitError("");
    setMultiSelectError("");

    const responseTypeValue = resolveResponseType(editingQuestion);
    setResponseType(responseTypeValue);
    setMultiSelectOptions(
      editingQuestion?.multi_select_options && Array.isArray(editingQuestion.multi_select_options)
        ? editingQuestion.multi_select_options
        : (responseTypeValue === "multi_select" ? ["", ""] : []),
    );

    if (editingQuestion) {
      const qName = editingQuestion.questionnaire_name || "";
      const qExists = questionnaireOptions.some((opt) => opt.value === qName && opt.value !== "custom");
      setIsCustomQuestionnaire(!qExists && qName !== "");
      lastQuestionnaireValueRef.current = qExists ? qName : "";

      const catKey = editingQuestion.category || "";
      const catExists = categoryOptions.some((opt) => opt.value === catKey && opt.value !== "custom");
      setIsCustomCategory(!catExists && catKey !== "");
      lastCategoryValueRef.current = catExists ? catKey : "";
    } else {
      setIsCustomQuestionnaire(false);
      lastQuestionnaireValueRef.current = "";
      setIsCustomCategory(false);
      lastCategoryValueRef.current = "";
    }
  }, [show, optionsReady, editingQuestion, questionnaireOptions, categoryOptions]);

  const handleClose = () => {
    setSubmitError("");
    onHide?.();
  };

  const handleQuestionnaireCancel = () => {
    setIsCustomQuestionnaire(false);
    formApiRef.current?.setValue("questionnaire_name", lastQuestionnaireValueRef.current || "");
  };

  const handleCategoryCancel = () => {
    setIsCustomCategory(false);
    formApiRef.current?.setValue("category", lastCategoryValueRef.current || "");
  };

  const handleResponseTypeChange = (value) => {
    setResponseType(value);
    setMultiSelectOptions((prev) => {
      if (value !== "multi_select") return [];
      return prev.length === 0 ? ["", ""] : prev;
    });
    setMultiSelectError("");
  };

  const handleMultiSelectOptionChange = (index, value) => {
    setMultiSelectOptions((prev) => prev.map((opt, i) => (i === index ? value : opt)));
    setMultiSelectError("");
  };

  const handleAddMultiSelectOption = () => {
    setMultiSelectOptions((prev) => [...prev, ""]);
  };

  const handleRemoveMultiSelectOption = (index) => {
    setMultiSelectOptions((prev) => prev.filter((_, i) => i !== index));
    setMultiSelectError("");
  };

  const handleFormSubmit = async (data) => {
    let validOptions = null;
    if (responseType === "multi_select") {
      validOptions = multiSelectOptions.filter((opt) => opt && opt.trim().length > 0).map((opt) => opt.trim());
      if (validOptions.length < 2) {
        setMultiSelectError("At least 2 options are required for multi-select questions");
        return;
      }
      if (validOptions.some((opt) => opt.length < 1 || opt.length > 255)) {
        setMultiSelectError("Each option must be between 1 and 255 characters");
        return;
      }
    }
    setMultiSelectError("");

    const finalQuestionnaireName = isCustomQuestionnaire ? (data.questionnaire_name_custom || "").trim() : data.questionnaire_name;
    const finalCategory = isCustomCategory ? (data.category_custom || "").trim() : data.category;

    setSaving(true);
    setSubmitError("");

    const payload = {
      question_text: data.question_text,
      response_type: responseType,
      weight: data.weight,
      ...(finalQuestionnaireName && { questionnaire_name: finalQuestionnaireName }),
      ...(finalCategory && { category: finalCategory }),
      ...(data.sub_category && { sub_category: data.sub_category }),
      ...(data.expected_evidence && { expected_evidence: data.expected_evidence }),
      ...(data.explanation && { explanation: data.explanation }),
      ...(responseType === "multi_select" && validOptions?.length > 0 && { multi_select_options: validOptions }),
    };

    try {
      const response = editingQuestion?.id
        ? await editThirdpartyQuestionsService(`${editingQuestion.id}/`, payload)
        : await createThirdpartyQuestionsService(payload);

      const successMessage = response?.statusCode?.response_data?.message || response?.statusCode?.message;
      const errorMessage = response?.errors?.message || response?.error?.message || response?.statusCode?.error?.message;

      if (successMessage && !errorMessage) {
        onSuccess?.();
      } else if (errorMessage) {
        setSubmitError(errorMessage);
      } else {
        setSubmitError(editingQuestion ? "Failed to update question. Please try again." : "Failed to create question. Please try again.");
      }
    } catch (error) {
      console.error("Error saving TPRM question:", error);
      setSubmitError(editingQuestion ? "Failed to update question. Please try again." : "Failed to create question. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const categoryKey = editingQuestion?.category || "";
  const categoryExists = categoryOptions.some((opt) => opt.value === categoryKey && opt.value !== "custom");
  const questionnaireName = editingQuestion?.questionnaire_name || "";
  const questionnaireExists = questionnaireOptions.some((opt) => opt.value === questionnaireName && opt.value !== "custom");

  const defaultValues = {
    questionnaire_name: editingQuestion ? (questionnaireExists ? questionnaireName : "") : "",
    questionnaire_name_custom: editingQuestion && !questionnaireExists ? questionnaireName : "",
    category: editingQuestion ? (categoryExists ? categoryKey : "") : "",
    category_custom: editingQuestion && !categoryExists ? categoryKey : "",
    sub_category: editingQuestion?.sub_category || "",
    question_text: editingQuestion?.question_text || "",
    expected_evidence: editingQuestion?.expected_evidence || "",
    explanation: editingQuestion?.explanation || "",
    weight: editingQuestion ? (editingQuestion.weight || editingQuestion.weight_display || 1) : "",
  };

  const questionnaireField = isCustomQuestionnaire
    ? {
      name: "questionnaire_name_custom",
      label: "Questionnaire Name",
      placeholder: "e.g., Default, Cloud Security Assessment, SOC2 Audit",
      required: "Questionnaire name is required",
      validate: (v) => !v || v.trim().toLowerCase() !== "default" || "Questionnaire name cannot be 'Default'. Please choose a different name.",
      style: CUSTOM_INPUT_STYLE,
      suffixButton: { text: "Cancel", onClick: handleQuestionnaireCancel, style: CUSTOM_CANCEL_BTN_STYLE },
    }
    : {
      name: "questionnaire_name",
      label: "Questionnaire Name",
      type: "select",
      required: "Questionnaire name is required",
      options: questionnaireOptions,
      placeholder: questionnaireOptions.length === 0 ? "No questionnaires available" : "Select questionnaire",
      onChange: (v) => { if (v === "custom") setIsCustomQuestionnaire(true); else lastQuestionnaireValueRef.current = v; },
    };

  const categoryField = isCustomCategory
    ? {
      name: "category_custom",
      label: "Category",
      placeholder: "Enter your custom category name",
      required: "Custom category name is required",
      validate: (v) => {
        const key = v.trim().toLowerCase().replace(/\s+/g, "_");
        return !allCategoryKeys.includes(key) || "This category already exists. Please choose a different name.";
      },
      style: CUSTOM_INPUT_STYLE,
      suffixButton: { text: "Cancel", onClick: handleCategoryCancel, style: CUSTOM_CANCEL_BTN_STYLE },
    }
    : {
      name: "category",
      label: "Category",
      type: "select",
      required: "Category is required",
      options: categoryOptions,
      placeholder: categoryOptions.length === 0 ? "No categories available" : "Select category",
      onChange: (v) => { if (v === "custom") setIsCustomCategory(true); else lastCategoryValueRef.current = v; },
    };

  const FIELDS = [
    questionnaireField,
    categoryField,
    { name: "sub_category", label: "Sub Category", required: "Sub category is required", placeholder: "Enter sub category" },
    { name: "question_text", label: "Question Text", type: "textarea", rows: 3, required: "Question text is required", placeholder: "Enter the question text" },
    { name: "expected_evidence", label: "Expected Evidence", required: "Expected evidence is required", placeholder: "e.g., Data flow diagram, architecture note" },
    { name: "explanation", label: "Explanation", type: "textarea", rows: 2, required: "Explanation is required", placeholder: "AI-generated explanation for the question to guide vendors" },
    { name: "weight", label: "Weight", type: "select", required: "Weight is required", options: WEIGHT_OPTIONS, placeholder: "Select weight" },
  ];

  return (
    <>
      {show && !optionsReady && (
        <div className="d-flex align-items-center justify-content-center" style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.35)", zIndex: 1060 }}>
          <div className="d-flex align-items-center gap-2" style={{ background: "#fff", padding: "16px 24px", borderRadius: 10, boxShadow: "0 12px 32px rgba(0,0,0,0.18)" }}>
            <ButtonLoader /> <span>Loading question form…</span>
          </div>
        </div>
      )}
      <CommonModal
        show={show && optionsReady}
        onHide={handleClose}
        title={editingQuestion ? "Edit TPRM Question" : "Create TPRM Question"}
        size="lg"
        error={submitError}
        fields={FIELDS}
        defaultValues={defaultValues}
        onSubmit={handleFormSubmit}
        formRef={formApiRef}
        buttons={[
          { text: "Cancel", variant: "secondary", onClick: handleClose, disabled: saving },
          { text: editingQuestion ? "Update Question" : "Create Question", variant: "primary", type: "submit", loading: saving, loadingText: "Saving…" },
        ]}
      >
        <div className="cmn-modal-field mb-3 tprm-response-type">
          <label className="cmn-modal-label">Response Type<span className="cmn-modal-req">*</span></label>
          <CommonDropdown
            ariaLabel="Response Type"
            value={responseType}
            onChange={handleResponseTypeChange}
            options={RESPONSE_TYPE_OPTIONS}
            style={{ width: "100%" }}
            minWidth={0}
          />
        </div>

        {responseType === "multi_select" && (
          <div className="cmn-modal-field mb-3">
            <label className="cmn-modal-label">Multi-select Options<span className="cmn-modal-req">*</span> (Minimum 2 required)</label>
            {multiSelectOptions.map((option, index) => (
              <div key={index} className="d-flex gap-2 mb-2">
                <div className="w-100">
                  <CommonInput
                    type="text"
                    value={option}
                    onChange={(e) => handleMultiSelectOptionChange(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                    maxLength={255}
                  />
                </div>
                <CommonButton
                  type="button"
                  text="Remove"
                  variant="error"
                  onClick={() => handleRemoveMultiSelectOption(index)}
                  disable={multiSelectOptions.length <= 2}
                />
              </div>
            ))}
            <CommonButton type="button" text="Add Option" variant="secondary" onClick={handleAddMultiSelectOption} className="mt-2" />
            {multiSelectError && <div className="text-danger mt-2">{multiSelectError}</div>}
            <div className="mt-2" style={{ fontSize: 13, opacity: 0.75 }}>
              Each option must be between 1 and 255 characters. At least 2 options are required.
            </div>
          </div>
        )}
      </CommonModal>
    </>
  );
}

export default CreateTPRMQuestionModal;
